// Plain files: a page read from markdown, and the site's restricted MDX (a
// line holding one self-closing tag with string props), printed back to the
// same bytes. No id goes in the file: each block keeps the lines it was read
// from beside its id in the doc. A block nobody changed prints those lines
// as they were; a changed one reprints only the span that changed, keeping
// the line breaks around it, and is read back to check it says the same.
// What this reader doesn't model (tables, quotes, front matter, HTML) is a
// `markdown` block holding its lines as text, edited as written.
// meets: file/round-trip, file/smallest-diff, file/reload-keeps, file/unknown-tags
import type { Delta } from "loro-crdt";
import type { NodeJSON, Spec, TreeID } from "./doc";

// How a block was written: its lines, and the blank lines before it.
export type Md = { src?: string; gap?: number };
export type MdPage = { nodes: Spec[]; tail: string };

const FENCE = /^( {0,3})(`{3,}|~{3,})(.*)$/;
const ATX = /^(#{1,6})(?: (.*))?$/;
// The site's tag: a capitalised name and string props, alone on its line.
const TAG = /^<([A-Z][A-Za-z]*)((?:\s+[a-z][A-Za-z-]*="[^"]*")*)\s*\/>\s*$/;
const HR = /^ {0,3}([-*_])(?: *\1){2,} *$/;
const ITEM = /^( {0,3})([-*+]|\d{1,9}[.)])( +|$)/;
const SETEXT = /^ {0,3}(=+|-+) *$/;
const DELIM_ROW = /^ *\|? *:?-+:? *(\| *:?-+:? *)*\|? *$/;
const lead = (l: string) => l.length - l.trimStart().length;
const blank = (l: string | undefined) => l !== undefined && l.trim() === "";

// A line that starts a block of its own, so it can't carry on a paragraph.
function interrupts(l: string): boolean {
  const t = l.trimStart();
  if (ATX.test(t) || FENCE.test(t) || TAG.test(t) || HR.test(t) || t.startsWith(">") || /^<[A-Za-z/!?]/.test(t)) return true;
  const m = t.match(ITEM);
  return !!m && t.slice(m[0].length).trim() !== "" && (!/\d/.test(m[2]) || /^1[.)]$/.test(m[2]));
}

// ---------- reading ----------

export function parseMarkdown(src: string): MdPage {
  const lines = src.split("\n");
  let end = lines.length;
  while (end > 0 && lines[end - 1] === "") end--;
  const body = lines.slice(0, end);
  const tail = src.slice(body.join("\n").length);
  const r = new Reader(body);
  const nodes = r.seq(0, true);
  const page = { nodes, tail };
  // Whatever this reader gets wrong, the file still comes back whole: as
  // one block of its own lines.
  if (printMarkdown(page.nodes.map(asJSON), tail) !== src) return { nodes: [raw(body, 0)], tail };
  return page;
}

const raw = (lines: string[], gap: number): Spec => ({ type: "markdown", text: lines.join("\n"), md: { src: lines.join("\n"), gap } });

class Reader {
  i = 0;
  constructor(readonly lines: string[]) {}

  // Blocks at or inside column `col`, until a line indented less.
  seq(col: number, top = false): Spec[] {
    const out: Spec[] = [];
    const L = this.lines;
    for (;;) {
      let j = this.i;
      while (j < L.length && blank(L[j])) j++;
      if (j >= L.length || (!top && lead(L[j]) < col)) return out;
      // Whitespace-only lines aren't blank lines here, so the gap is exact.
      for (let k = this.i; k < j; k++) if (L[k] !== "") return (out.push(raw(L.slice(this.i, j), 0)), (this.i = j), out);
      const gap = j - this.i;
      this.i = j;
      out.push(this.block(col, gap, top && !out.length && j === 0));
    }
  }

  block(col: number, gap: number, first: boolean): Spec {
    const L = this.lines;
    const line = L[this.i];
    const v = line.slice(Math.min(col, lead(line)));
    if (first && line === "---") {
      const close = L.indexOf("---", this.i + 1);
      if (close > 0) return this.take(close + 1, gap);
    }
    const fence = v.match(FENCE);
    if (fence) {
      const [, ind, mark] = fence;
      let j = this.i + 1;
      const closer = new RegExp(`^ {0,3}${mark[0] === "`" ? "`" : "~"}{${mark.length},} *$`);
      while (j < L.length && !closer.test(L[j].slice(Math.min(col, lead(L[j]))))) j++;
      const src = L.slice(this.i, Math.min(j + 1, L.length));
      this.i = Math.min(j + 1, L.length);
      const inner = src.slice(1, j < L.length ? -1 : undefined);
      const cut = col + ind.length;
      return { type: "code", props: { lang: fence[3].trim() }, text: inner.map((l) => l.slice(Math.min(cut, lead(l)))).join("\n"), md: { src: src.join("\n"), gap } };
    }
    const h = v.match(ATX);
    if (h) {
      this.i++;
      return { type: "heading", props: { level: String(h[1].length) }, delta: parseInline(h[2] ?? ""), md: { src: line, gap } };
    }
    const tag = v.match(TAG);
    if (tag) {
      this.i++;
      return { type: tag[1], props: tagProps(tag[2]), md: { src: line, gap } };
    }
    if (HR.test(v)) return this.take(this.i + 1, gap);
    if (v.includes("|") && DELIM_ROW.test(L[this.i + 1] ?? "") && L[this.i + 1].includes("-")) return this.table(gap);
    if (/^ {0,3}(>|<)/.test(v)) return this.until(gap);
    if (v.match(ITEM) && !HR.test(v)) return this.list(col, gap);
    if (/^ {4}/.test(v)) {
      let j = this.i;
      while (j < L.length && !blank(L[j]) && lead(L[j]) >= col + 4) j++;
      return this.take(j, gap);
    }
    // A paragraph: lines until a blank one or one that starts a block.
    let j = this.i + 1;
    while (j < L.length && !blank(L[j]) && !interrupts(L[j]) && !(L[j].includes("|") && DELIM_ROW.test(L[j + 1] ?? "") && L[j + 1].includes("-"))) {
      if (SETEXT.test(L[j]) && lead(L[j]) >= col) return this.take(j + 1, gap);
      j++;
    }
    const src = L.slice(this.i, j);
    this.i = j;
    return { type: "paragraph", delta: parseInline(joined(src)), md: { src: src.join("\n"), gap } };
  }

  // Lines as written, to `end` or the next blank line.
  take(end: number, gap: number): Spec {
    const src = this.lines.slice(this.i, end);
    this.i = end;
    return raw(src, gap);
  }
  until(gap: number): Spec {
    let j = this.i;
    while (j < this.lines.length && !blank(this.lines[j])) j++;
    return this.take(j, gap);
  }

  // A table: a row per line, a cell per column; the line under the header
  // is the table's own, for its alignment and widths.
  table(gap: number): Spec {
    const L = this.lines;
    const head = L[this.i];
    const delim = L[this.i + 1];
    const rows: Spec[] = [row(head)];
    this.i += 2;
    while (this.i < L.length && !blank(L[this.i]) && L[this.i].includes("|") && !interrupts(L[this.i])) rows.push(row(L[this.i++]));
    return { type: "table", md: { src: delim, gap }, children: rows };
  }

  list(col: number, gap: number): Spec {
    const L = this.lines;
    const first = L[this.i].slice(col).match(ITEM)!;
    const kind = listKind(first[2]);
    const items: Spec[] = [];
    let itemGap = 0;
    for (;;) {
      const line = L[this.i];
      const m = line.slice(col).match(ITEM)!;
      const content = line.slice(col + m[0].length);
      const spaces = m[3].length;
      const c = col + m[1].length + m[2].length + (spaces >= 1 && spaces <= 4 && content.trim() ? spaces : 1);
      const src = [line];
      this.i++;
      // Its words: lines after it, indented or lazy, until a block starts.
      while (this.i < L.length && !blank(L[this.i])) {
        const l = L[this.i];
        const inner = lead(l) >= c ? l.slice(c) : l;
        if (interrupts(inner) || (lead(l) < c && l.trimStart().match(ITEM))) break;
        src.push(l);
        this.i++;
      }
      const children = this.seq(c);
      items.push({ type: "item", delta: parseInline(joined(src, line.length - content.length)), md: { src: src.join("\n"), gap: itemGap }, children });
      // Another item of this list, after any blank lines?
      let j = this.i;
      while (j < L.length && blank(L[j])) j++;
      const next = L[j];
      const nm = next !== undefined && lead(next) >= col && lead(next) < c ? next.slice(col).match(ITEM) : null;
      if (!nm || listKind(nm[2]) !== kind || HR.test(next.slice(col)) || L.slice(this.i, j).some((l) => l !== "")) break;
      itemGap = j - this.i;
      this.i = j;
    }
    return { type: "list", props: { ordered: kind !== "-" && kind !== "*" && kind !== "+" }, md: { gap }, children: items };
  }
}

// A table line's cells, split on the pipes that aren't escaped.
function cells(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|") && !t.endsWith("\\|")) t = t.slice(0, -1);
  const out = [""];
  for (let i = 0; i < t.length; i++) {
    if (t[i] === "\\" && t[i + 1] === "|") (out[out.length - 1] += "\\|"), i++;
    else if (t[i] === "|") out.push("");
    else out[out.length - 1] += t[i];
  }
  return out;
}
const row = (line: string): Spec => ({ type: "row", md: { src: line }, children: cells(line).map((c) => ({ type: "cell", delta: cellInline(c) })) });
// A cell's words; an escaped pipe is a pipe, in code too.
const cellInline = (c: string) =>
  parseInline(c.trim()).map((op) => ("insert" in op && (op.attributes as Attrs | undefined)?.code ? { ...op, insert: op.insert!.replace(/\\\|/g, "|") } : op)) as Delta<string>[];

const listKind = (marker: string) => (/\d/.test(marker) ? marker.slice(-1) : marker);

function tagProps(attrs: string): Record<string, string> {
  return Object.fromEntries([...attrs.matchAll(/([a-z][A-Za-z-]*)="([^"]*)"/g)].map((p) => [p[1], p[2]]));
}

// A block's lines as one run of words: its first line after `from`, the
// rest without their indent, joined as markdown joins them.
function joined(lines: string[], from = 0): string {
  return contents(lines, from).join(" ");
}
function contents(lines: string[], from: number): string[] {
  return lines.map((l, k) => (k ? l.trimStart() : l.slice(from)));
}

// ---------- printing ----------

export function printMarkdown(nodes: NodeJSON[], tail = "\n"): string {
  const out = seqLines(nodes, 0, "top");
  return out.join("\n") + (out.length ? tail : tail.replace(/^\n/, ""));
}

type Where = "top" | "item" | "list";
const isTag = (n: NodeJSON) => /^[A-Z]/.test(n.type) && n.text === undefined && !n.children.length;

function seqLines(nodes: NodeJSON[], col: number, where: Where): string[] {
  const out: string[] = [];
  nodes.forEach((n, k) => {
    const gap = n.md?.gap ?? defaultGap(nodes, k, where);
    if (out.length || k || where === "top") for (let g = 0; g < gap; g++) out.push("");
    out.push(...nodeLines(n, col, nodes, k));
  });
  return out;
}

function defaultGap(nodes: NodeJSON[], k: number, where: Where): number {
  if (where === "top") return k ? 1 : 0;
  if (where === "list") return k ? nodes[k - 1].md?.gap ?? 0 : 0;
  return nodes[k].type === "list" ? 0 : 1;
}

// One block's own lines, and its children's.
export function nodeLines(n: NodeJSON, col: number, siblings: NodeJSON[] = [n], k = 0): string[] {
  const src = n.md?.src?.split("\n");
  const pad = " ".repeat(col);
  switch (n.type) {
    case "heading": {
      const level = Math.min(6, Math.max(1, Number(n.props.level) || 2));
      const prefix = "#".repeat(level) + " ";
      const same = src && src.length === 1 && src[0].trimStart().startsWith(prefix) ? src : undefined;
      return textLines(n.text ?? [], same, same ? same[0].slice(0, lead(same[0]) + prefix.length) : pad + prefix, pad, false);
    }
    case "paragraph":
      return textLines(n.text ?? [], src, src ? src[0].slice(0, lead(src[0])) : pad, src?.[1] !== undefined ? src[1].slice(0, lead(src[1])) : pad, true);
    case "list":
      return n.children.flatMap((item, i) => {
        const gap = i ? item.md?.gap ?? n.children[i - 1].md?.gap ?? 0 : 0;
        return [...Array(gap).fill(""), ...itemLines(item, col, n, i)];
      });
    case "item":
      // An item outside a list prints as a list of one.
      return itemLines(n, col, { ...n, type: "list", props: {}, children: [n] } as NodeJSON, 0);
    case "code":
      return codeLines(n, src, pad);
    case "table":
      return tableLines(n, pad);
    case "row":
      return tableLines({ ...n, type: "table", md: undefined, children: [n] } as NodeJSON, pad).slice(0, 1);
    case "cell":
      return [pad + printInline(n.text ?? [], true)];
    case "markdown": {
      const text = plainOf(n.text);
      return (text === "" ? [] : text.split("\n")).concat(n.children.length ? seqLines(n.children, col, "item") : []);
    }
  }
  if (isTag(n)) {
    if (src?.length === 1 && sameProps(tagProps(src[0].match(TAG)?.[2] ?? ""), n.props) && TAG.test(src[0].trimStart())) return src;
    const order = [...Object.keys(src ? tagProps(src[0]) : {}), ...Object.keys(n.props).sort()];
    const attrs = [...new Set(order)].filter((k) => k in n.props && /^[a-z][A-Za-z-]*$/.test(k)).map((k) => ` ${k}="${String(n.props[k]).replace(/"/g, "'").replace(/\n/g, " ")}"`).join("");
    return [`${src ? src[0].slice(0, lead(src[0])) : pad}<${n.type}${attrs} />`];
  }
  // Any other component: a container leaves its blocks; words read as a
  // paragraph; anything else is a tag.
  if (n.text !== undefined) return [...textLines(n.text, undefined, pad, pad, true), ...(n.children.length ? ["", ...seqLines(n.children, col, "top")] : [])];
  if (n.children.length) return seqLines(n.children, col, "top");
  const attrs = Object.keys(n.props).sort().map((k) => ` ${k}="${String(n.props[k]).replace(/"/g, "'")}"`).join("");
  return [`${pad}<${n.type}${attrs} />`];
}

function sameProps(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  return ka.join() === kb.join() && ka.every((k) => a[k] === b[k]);
}

function itemLines(item: NodeJSON, col: number, list: NodeJSON, i: number): string[] {
  const src = item.md?.src?.split("\n");
  const m = src?.[0].slice(Math.min(col, lead(src[0]))).match(ITEM);
  let prefix: string;
  if (src && m) prefix = src[0].slice(0, Math.min(col, lead(src[0])) + m[0].length) || "- ";
  else {
    // A new item writes its marker as the items beside it do.
    const near = [...list.children.slice(0, i).reverse(), ...list.children.slice(i + 1)].find((s) => s.md?.src);
    const nm = near?.md?.src?.split("\n")[0].slice(col).match(ITEM);
    const ordered = list.props.ordered === true;
    let marker = ordered ? "1." : "-";
    if (nm && ordered === /\d/.test(nm[2])) {
      if (!ordered) marker = nm[2];
      else {
        const before = list.children.slice(0, i).reverse().find((s) => s.md?.src);
        const at = list.children.indexOf(before!);
        const bm = before?.md?.src?.split("\n")[0].slice(col).match(ITEM);
        marker = `${bm ? Number(bm[2].slice(0, -1)) + (i - at) : i + 1}${nm[2].slice(-1)}`;
      }
    } else if (ordered) marker = `${i + 1}.`;
    prefix = " ".repeat(col + (nm?.[1].length ?? 0)) + marker + " ";
  }
  const c = prefix.length;
  const cont = src?.[1] !== undefined ? src[1].slice(0, lead(src[1])) : " ".repeat(c);
  return [...textLines(item.text ?? [], src && m ? src : undefined, prefix, cont, true), ...seqLines(item.children, c, "item")];
}

// A row as read if its cells are; else in its table's style: the pipes at
// its ends or not, and each cell padded to its column's width when the
// table's rows are.
function tableLines(t: NodeJSON, pad: string): string[] {
  const rows = t.children;
  const cols = Math.max(1, ...rows.map((r) => r.children.length));
  const delim = t.md?.src ?? `${pad}|${Array(cols).fill(" --- ").join("|")}|`;
  const segs = cells(delim);
  const ends = { start: /^\s*\|/.test(delim), end: /\|\s*$/.test(delim) };
  const indent = delim.slice(0, lead(delim));
  // Padded if a row as read has every cell its column's width.
  const read = rows.find((r) => r.md?.src);
  const padded = !!read && cells(read.md!.src!).every((c, i) => segs[i] === undefined || c.length === segs[i].length);
  const line = (r: NodeJSON) => {
    const src = r.md?.src;
    if (src) {
      const was = cells(src);
      if (was.length === r.children.length && r.children.every((c, i) => deltaEq(cellInline(was[i]), c.text ?? []))) return src;
    }
    const body = r.children.map((c, i) => {
      const text = printInline(c.text ?? [], true);
      const w = padded && segs[i] ? segs[i].length - 2 : 0;
      return ` ${text.padEnd(w)} `;
    });
    return indent + (ends.start ? "|" : "") + body.join("|") + (ends.end ? "|" : "");
  };
  return rows.length ? [line(rows[0]), delim, ...rows.slice(1).map(line)] : [];
}

function codeLines(n: NodeJSON, src: string[] | undefined, pad: string): string[] {
  const text = plainOf(n.text);
  const lang = String(n.props.lang ?? "");
  const fence = src?.[0].match(FENCE);
  if (src && fence && fence[3].trim() === lang) {
    const r = new Reader(src);
    const read = r.block(lead(src[0]) - fence[1].length, 0, false);
    if (read.text === text) return src;
    const ind = src[0].slice(0, lead(src[0]));
    const closed = src.length > 1 && new RegExp(`^ *${fence[2][0] === "`" ? "`" : "~"}{${fence[2].length},} *$`).test(src[src.length - 1]);
    return [src[0], ...text.split("\n").map((l) => (l ? ind + l : l)), ...(closed ? [src[src.length - 1]] : [])];
  }
  let ticks = "```";
  while (text.includes(ticks)) ticks += "`";
  return [pad + ticks + lang, ...text.split("\n").map((l) => (l ? pad + l : l)), pad + ticks];
}

// A block of words: its lines as read if its words are as read; else the
// lines it was read from with only the changed span reprinted; else one line.
function textLines(delta: Delta<string>[], src: string[] | undefined, prefix: string, cont: string, guard: boolean): string[] {
  const S = printInline(delta);
  const fresh = [prefix + (guard ? guardStart(S) : S)];
  if (!src) return fresh;
  const parts = contents(src, prefix.length);
  const M = parts.join(" ");
  if (deltaEq(parseInline(M), delta)) return src;
  const old = parts.join("\n");
  let p = 0;
  while (p < M.length && p < S.length && M[p] === S[p]) p++;
  let q = 0;
  while (q < M.length - p && q < S.length - p && M[M.length - 1 - q] === S[S.length - 1 - q]) q++;
  const merged = (old.slice(0, p) + S.slice(p, S.length - q) + old.slice(old.length - q)).split("\n");
  // The line breaks kept must still read as the same words, and as one block.
  const ok =
    merged.every((l, i) => l.trim() !== "" && (i === 0 || (!interrupts(l) && l === l.trimStart() && !SETEXT.test(l)))) &&
    (!guard || guardStart(merged[0]) === merged[0]) &&
    deltaEq(parseInline(merged.join(" ")), parseInline(S));
  return ok ? merged.map((l, i) => (i ? cont : prefix) + l) : fresh;
}

// What would start another block at the start of a line, escaped.
function guardStart(s: string): string {
  if (/^(#{1,6}( |$)|[-+*] |[-+*]$|>|[=-]+ *$|\d{1,9}[.)]( |$))/.test(s)) {
    const m = s.match(/^\d{1,9}/);
    return m ? m[0] + "\\" + s.slice(m[0].length) : "\\" + s;
  }
  return s;
}

const plainOf = (d: Delta<string>[] | undefined) => (d ?? []).map((o) => ("insert" in o ? o.insert ?? "" : "")).join("");

// ---------- words ----------

type Attrs = { bold?: true; italic?: true; code?: true; link?: string };
type Ch = { c: string; a: Attrs };
const PUNCT = /[!-/:-@[-`{-~]/;
const ws = (c: string | undefined) => c === undefined || /\s/.test(c);
const punct = (c: string | undefined) => c !== undefined && /[\p{P}\p{S}]/u.test(c);

// Inline markdown to a delta: **bold**, *italic* or _italic_, `code`,
// [links](address) and backslash escapes. What doesn't parse is its own
// characters.
export function parseInline(s: string): Delta<string>[] {
  const out: Ch[] = [];
  inline(s, 0, s.length, {}, out);
  return toDelta(out);
}

function inline(s: string, from: number, to: number, base: Attrs, out: Ch[]) {
  const on: { bold?: string; italic?: string } = {};
  const push = (text: string, extra: Attrs = {}) => {
    const a: Attrs = { ...base, ...extra };
    if (on.bold) a.bold = true;
    if (on.italic) a.italic = true;
    for (const c of text) out.push({ c, a });
  };
  for (let i = from; i < to; ) {
    const c = s[i];
    if (c === "\\" && i + 1 < to && PUNCT.test(s[i + 1])) {
      push(s[i + 1]);
      i += 2;
    } else if (c === "`") {
      const n = run(s, i, to, "`");
      const close = findTicks(s, i + n, to, n);
      if (close < 0) {
        push("`".repeat(n));
        i += n;
        continue;
      }
      let code = s.slice(i + n, close);
      if (code.length > 1 && code[0] === " " && code[code.length - 1] === " " && code.trim()) code = code.slice(1, -1);
      push(code, { code: true });
      i = close + n;
    } else if (c === "[") {
      const link = linkAt(s, i, to);
      if (!link) {
        push("[");
        i++;
        continue;
      }
      const a: Attrs = { ...base, link: link.href };
      if (on.bold) a.bold = true;
      if (on.italic) a.italic = true;
      inline(s, i + 1, link.textEnd, a, out);
      i = link.end;
    } else if (c === "*" || c === "_") {
      const n = run(s, i, to, c);
      const prev = s[i - 1];
      const next = s[i + n];
      const left = !ws(next) && (!punct(next) || ws(prev) || punct(prev));
      const right = !ws(prev) && (!punct(prev) || ws(next) || punct(next));
      const open = c === "*" ? left : left && (!right || punct(prev));
      const close = c === "*" ? right : right && (!left || punct(next));
      let rem = n;
      while (rem > 0) {
        if (on.bold === c && rem >= 2 && close) (on.bold = undefined), (rem -= 2);
        else if (on.italic === c && close) (on.italic = undefined), (rem -= 1);
        else if (!on.bold && rem >= 2 && open && closer(s, i + n, to, c, 2)) (on.bold = c), (rem -= 2);
        else if (!on.italic && open && closer(s, i + n, to, c, 1)) (on.italic = c), (rem -= 1);
        else break;
      }
      if (rem) push(c.repeat(rem));
      i += n;
    } else {
      push(c);
      i++;
    }
  }
}

function run(s: string, i: number, to: number, c: string): number {
  let n = 0;
  while (i + n < to && s[i + n] === c) n++;
  return n;
}

function findTicks(s: string, from: number, to: number, n: number): number {
  for (let j = from; j < to; ) {
    if (s[j] !== "`") {
      j++;
      continue;
    }
    const m = run(s, j, to, "`");
    if (m === n) return j;
    j += m;
  }
  return -1;
}

// Whether a run of `c` later on could close one opened here.
function closer(s: string, from: number, to: number, c: string, n: number): boolean {
  for (let j = from; j < to; j++) {
    if (s[j] === "\\") j++;
    else if (s[j] === "`") {
      const m = run(s, j, to, "`");
      const end = findTicks(s, j + m, to, m);
      j = end < 0 ? j + m - 1 : end + m - 1;
    } else if (s[j] === c) {
      const m = run(s, j, to, c);
      if (m >= n && !ws(s[j - 1])) return true;
      j += m - 1;
    }
  }
  return false;
}

// [words](address): where the words end, the address, and where it ends.
function linkAt(s: string, i: number, to: number): { textEnd: number; href: string; end: number } | undefined {
  let depth = 0;
  let j = i;
  for (; j < to; j++) {
    if (s[j] === "\\") j++;
    else if (s[j] === "`") {
      const m = run(s, j, to, "`");
      const end = findTicks(s, j + m, to, m);
      j = end < 0 ? j + m - 1 : end + m - 1;
    } else if (s[j] === "[") depth++;
    else if (s[j] === "]" && --depth === 0) break;
  }
  if (j >= to || s[j + 1] !== "(") return undefined;
  let k = j + 2;
  let parens = 0;
  if (s[k] === "<") {
    while (k < to && s[k] !== ">") k++;
    k++;
  }
  for (; k < to; k++) {
    if (s[k] === "\\") k++;
    else if (s[k] === "(") parens++;
    else if (s[k] === ")" && parens-- === 0) break;
  }
  if (k >= to) return undefined;
  return { textEnd: j, href: s.slice(j + 2, k), end: k + 1 };
}

function toDelta(chars: Ch[]): Delta<string>[] {
  const out: { insert: string; attributes?: Record<string, unknown> }[] = [];
  for (const { c, a } of chars) {
    const attributes = Object.keys(a).length ? a : undefined;
    const prev = out[out.length - 1];
    if (prev && JSON.stringify(prev.attributes) === JSON.stringify(attributes)) prev.insert += c;
    else out.push(attributes ? { insert: c, attributes: { ...a } } : { insert: c });
  }
  return out as Delta<string>[];
}

function charsOf(delta: Delta<string>[]): Ch[] {
  const out: Ch[] = [];
  for (const op of delta) {
    if (!("insert" in op) || !op.insert) continue;
    const src = (op.attributes ?? {}) as Record<string, unknown>;
    const a: Attrs = {};
    if (src.bold) a.bold = true;
    if (src.italic) a.italic = true;
    if (src.code) a.code = true;
    if (typeof src.link === "string") a.link = src.link;
    for (const c of op.insert) out.push({ c: c === "\n" ? " " : c, a });
  }
  return out;
}

// Two runs of words are the same if their characters and marks are.
export function deltaEq(a: Delta<string>[], b: Delta<string>[]): boolean {
  const key = (d: Delta<string>[]) => JSON.stringify(charsOf(d).map(({ c, a }) => [c, a.bold ?? 0, a.italic ?? 0, a.code ?? 0, a.link ?? 0]));
  return key(a) === key(b);
}

// A delta as inline markdown, written as these files write it.
export function printInline(delta: Delta<string>[], pipes = false): string {
  const chars = charsOf(delta);
  // Bold and italic can't start or end on a space in markdown: the spaces
  // go outside them.
  for (const k of ["bold", "italic"] as const) {
    for (let i = 0; i < chars.length; i++) {
      if (!chars[i].a[k]) continue;
      let j = i;
      while (j < chars.length && chars[j].a[k]) j++;
      for (let x = i; x < j && ws(chars[x].c); x++) chars[x].a = { ...chars[x].a, [k]: undefined };
      for (let x = j - 1; x >= i && ws(chars[x].c); x--) chars[x].a = { ...chars[x].a, [k]: undefined };
      i = j;
    }
  }
  let out = "";
  for (let i = 0; i < chars.length; ) {
    const link = chars[i].a.link;
    let j = i;
    while (j < chars.length && chars[j].a.link === link) j++;
    const inner = marks(chars.slice(i, j), pipes);
    out += link === undefined ? inner : `[${inner}](${/[\s<>]/.test(link) && !link.startsWith("<") ? `<${link}>` : link})`;
    i = j;
  }
  return out;
}

function marks(chars: Ch[], pipes = false): string {
  let out = "";
  const on = { bold: false, italic: false };
  const set = (bold: boolean, italic: boolean) => {
    if (on.italic && !italic) (out += "*"), (on.italic = false);
    if (on.bold && !bold) {
      if (on.italic) (out += "*"), (on.italic = false);
      (out += "**"), (on.bold = false);
    }
    if (bold && !on.bold) (out += "**"), (on.bold = true);
    if (italic && !on.italic) (out += "*"), (on.italic = true);
  };
  for (let i = 0; i < chars.length; ) {
    const a = chars[i].a;
    set(!!a.bold, !!a.italic);
    let j = i;
    while (j < chars.length && !!chars[j].a.bold === !!a.bold && !!chars[j].a.italic === !!a.italic && !!chars[j].a.code === !!a.code) j++;
    const text = chars.slice(i, j).map((x) => x.c).join("");
    out += a.code ? codeSpan(text, pipes) : escape(text, chars[i - 1]?.c, chars[j]?.c, pipes);
    i = j;
  }
  set(false, false);
  return out;
}

function codeSpan(text: string, pipes = false): string {
  let n = 1;
  for (const m of text.match(/`+/g) ?? []) if (m.length >= n) n = m.length + 1;
  const ticks = "`".repeat(n);
  const pad = /^`|`$/.test(text) || (text.length > 1 && /^ .* $/.test(text) && text.trim()) ? " " : "";
  // In a table a pipe ends the cell, even in code.
  return ticks + pad + (pipes ? text.replace(/\|/g, "\\|") : text) + pad + ticks;
}

// Escape what would otherwise mean something, and nothing else, so an edit
// reprints its neighbours as they were written.
function escape(s: string, before?: string, after?: string, pipes = false): string {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    const prev = i ? s[i - 1] : before;
    const next = i + 1 < s.length ? s[i + 1] : after;
    const word = (x?: string) => x !== undefined && /[\p{L}\p{N}]/u.test(x);
    if (c === "*" || c === "`" || c === "[" || c === "]" || (pipes && c === "|")) out += "\\" + c;
    else if (c === "_" && !(word(prev) && word(next))) out += "\\_";
    else if (c === "\\" && (next === undefined || PUNCT.test(next))) out += "\\\\";
    else if (c === "<" && next !== undefined && /[A-Za-z/!?]/.test(next)) out += "\\<";
    else out += c;
  }
  return out;
}

// ---------- reloading ----------

// Give the blocks read from a file the ids of the blocks they were: the
// same words in the same order keep their ids, and a changed block between
// two that match keeps the id of the one of its kind it replaced. So a
// file changed on disk comes in as the smallest edit, and comments and
// history stay on their blocks.
export function keepIds(have: NodeJSON[], want: Spec[]) {
  const a: NodeJSON[] = [];
  const walkA = (ns: NodeJSON[]) => ns.forEach((n) => (a.push(n), walkA(n.children)));
  walkA(have);
  const b: Spec[] = [];
  const walkB = (ns: Spec[]) => ns.forEach((s) => (b.push(s), walkB(s.children ?? [])));
  walkB(want);
  const ka = a.map((n) => `${n.type}\u0000${JSON.stringify(n.props)}\u0000${plainOf(n.text)}`);
  const kb = b.map((s) => `${s.type}\u0000${JSON.stringify(s.props ?? {})}\u0000${s.delta ? plainOf(s.delta) : s.text ?? ""}`);
  const pairs = lcs(ka, kb);
  const used = new Set<TreeID>();
  const give = (s: Spec, n: NodeJSON) => {
    if (used.has(n.id)) return;
    used.add(n.id);
    s.id = n.id;
  };
  // Between two matches, blocks of one kind pair up in order.
  let pa = 0;
  let pb = 0;
  for (const [x, y] of [...pairs, [a.length, b.length]]) {
    const ga = a.slice(pa, x);
    const gb = b.slice(pb, y);
    for (const s of gb) {
      const i = ga.findIndex((n) => n.type === s.type && !used.has(n.id));
      if (i >= 0) give(s, ga.splice(i, 1)[0]);
    }
    if (x < a.length) give(b[y], a[x]);
    pa = x + 1;
    pb = y + 1;
  }
}

// The longest common subsequence of two lists of keys, as index pairs.
function lcs(a: string[], b: string[]): [number, number][] {
  // Equal ends first, so a long file with a small change stays cheap.
  let s = 0;
  while (s < a.length && s < b.length && a[s] === b[s]) s++;
  let e = 0;
  while (e < a.length - s && e < b.length - s && a[a.length - 1 - e] === b[b.length - 1 - e]) e++;
  const A = a.slice(s, a.length - e);
  const B = b.slice(s, b.length - e);
  const mid: [number, number][] = [];
  if (A.length * B.length <= 4_000_000) {
    const w = B.length + 1;
    const t = new Uint32Array((A.length + 1) * w);
    for (let i = A.length - 1; i >= 0; i--)
      for (let j = B.length - 1; j >= 0; j--) t[i * w + j] = A[i] === B[j] ? t[(i + 1) * w + j + 1] + 1 : Math.max(t[(i + 1) * w + j], t[i * w + j + 1]);
    for (let i = 0, j = 0; i < A.length && j < B.length; ) {
      if (A[i] === B[j]) mid.push([s + i++, s + j++]);
      else if (t[(i + 1) * w + j] >= t[i * w + j + 1]) i++;
      else j++;
    }
  }
  const head = Array.from({ length: s }, (_, i) => [i, i] as [number, number]);
  const foot = Array.from({ length: e }, (_, i) => [a.length - e + i, b.length - e + i] as [number, number]);
  return [...head, ...mid, ...foot];
}

// A spec as the printer reads a node, for checking a read before any doc.
function asJSON(s: Spec): NodeJSON {
  return {
    id: "" as TreeID,
    type: s.type,
    props: s.props ?? {},
    style: s.style ?? {},
    ...(s.delta ? { text: s.delta } : s.text !== undefined ? { text: s.text ? [{ insert: s.text }] : [] } : {}),
    children: (s.children ?? []).map(asJSON),
    ...(s.md ? { md: s.md } : {}),
  } as NodeJSON;
}
